import { useId } from 'react';
import { HelpTip } from '../HelpTip';
import { useDeferredError } from './useDeferredError';

/**
 * Which way a signed quantity moves. The numbers are `StockAdjustmentDirection`'s own.
 *
 * Declared here rather than imported from a feature, because a shared input may not depend on
 * one — and the pair is the concept this control exists to express, not that module's enum.
 */
export const SIGN_DIRECTION = {
  add: 0,
  deduct: 1,
} as const;

export type SignDirection = (typeof SIGN_DIRECTION)[keyof typeof SIGN_DIRECTION];

/**
 * A DIRECTION and a MAGNITUDE joined into one control — `+` / `−` on the left of the number.
 *
 * <b>Product call, 29 Sep 2026.</b> The source this was captured for uses a plain signed input,
 * and a signed input is the thing this product already refuses one module along: "a disposal is
 * ASKED for as a positive number and WRITTEN as a negative one", because two spellings of one
 * number in a single request is how a caller eventually passes the wrong one. A dropped minus is
 * indistinguishable from a real entry — it would ADD thirty where the reader meant to remove
 * them, and every figure downstream would read as entirely ordinary.
 *
 * <b>It is `<FieldGroup>`'s composition rather than a second treatment</b>: the wrapper owns the
 * border, the radius and the focus ring, the segments are borderless, and a `border-l` divides
 * them — so a joined sign-and-number reads as the same kind of control as a joined surname and
 * suffix, and as the toolbar's own `<SearchGroup>`. What it is NOT is a `<FieldGroup>` call: that
 * component's segments are all text inputs, and the leading segment here is a two-state toggle.
 *
 * Every promise the other shared inputs make still holds — one label line plus the control so a
 * row of fields aligns, the description in a `<HelpTip>` with an `sr-only` twin wired to
 * `aria-describedby`, and the error below the control in the flow, deferred so a required-but-
 * empty field never opens red.
 */
export function SignedNumberField({
  id,
  label,
  description,
  direction,
  onDirectionChange,
  value,
  onValueChange,
  error,
  required,
  disabled,
  placeholder = '—',
  min = 0,
  max,
  addLabel = 'Add',
  deductLabel = 'Deduct',
}: {
  /**
   * The NUMBER's own DOM id, and it must match the server's field name through the
   * `${idPrefix}-${kebab(field)}` convention — `focusValidationError` lands on the control the
   * server named. The direction toggle derives its ids from this one, so a rejection naming
   * either half has something to land on.
   */
  id: string;
  label: string;
  description?: string;
  /** Null is unanswered — neither side is pre-selected. */
  direction: SignDirection | null;
  onDirectionChange: (direction: SignDirection | null) => void;
  value: string;
  onValueChange: (value: string) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  min?: number;
  max?: number;
  addLabel?: string;
  deductLabel?: string;
}) {
  const generatedId = useId();
  const descriptionId = `${generatedId}-desc`;
  const directionId = `${id}-direction`;

  const { error: shown, markTouched } = useDeferredError(error);
  const invalid = shown !== undefined;

  const describedBy = [description ? descriptionId : null, shown ? `${id}-err` : null]
    .filter(Boolean)
    .join(' ');

  /*
    A two-state toggle rather than a `<select>`: the choice is binary and the glyphs are one
    character each, so a dropdown would hide half the control's meaning behind a click. It is
    `role="radiogroup"` because that is what it is — two mutually exclusive options, one of which
    may be unanswered — and NOT a pair of checkboxes, which would let both be ticked.
  */
  const segment = (target: SignDirection, glyph: string, text: string) => {
    const selected = direction === target;

    return (
      <button
        key={target}
        type="button"
        id={`${directionId}-${target}`}
        role="radio"
        aria-checked={selected}
        aria-label={text}
        disabled={disabled}
        onClick={() => {
          markTouched();
          // Pressing the selected side again CLEARS it, so a reader who set a direction by
          // accident can get back to unanswered without reloading the form.
          onDirectionChange(selected ? null : target);
        }}
        className={[
          'w-9 shrink-0 text-sm font-medium transition',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-300',
          'disabled:cursor-not-allowed disabled:text-slate-300',
          // The SELECTED side is the darkest thing in the control and carries a tint behind it
          // — never a hue on the glyph, which would read as a link. One selection treatment.
          selected ? 'bg-blue-50 text-slate-900' : 'text-slate-500 hover:bg-slate-50',
          target === SIGN_DIRECTION.add ? 'rounded-l-lg' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span aria-hidden="true">{glyph}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* Labels are regular weight and exactly one line tall, so a row of fields aligns. */}
      <div className="flex min-h-5 items-center gap-1.5">
        <label htmlFor={id} className="text-sm text-slate-700">
          {label}
          {required ? (
            <span className="ml-0.5 text-red-600" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>

        {description ? <HelpTip label={description} /> : null}
      </div>

      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}

      <div
        className={[
          'flex min-h-10 items-stretch overflow-hidden rounded-lg border bg-white transition',
          invalid
            ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100'
            : 'border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100',
        ].join(' ')}
      >
        <div
          role="radiogroup"
          id={directionId}
          aria-label={`${label} direction`}
          className={[
            'flex items-stretch divide-x',
            invalid ? 'divide-red-400' : 'divide-slate-300',
          ].join(' ')}
        >
          {segment(SIGN_DIRECTION.add, '+', addLabel)}
          {segment(SIGN_DIRECTION.deduct, '−', deductLabel)}
        </div>

        <input
          id={id}
          name={id}
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onValueChange(e.target.value)}
          onBlur={markTouched}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={shown ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            'min-w-0 flex-1 border-0 border-l bg-transparent px-3 py-2',
            'font-mono text-sm tabular-nums text-slate-600',
            invalid ? 'border-l-red-400' : 'border-l-slate-300',
            'placeholder:font-sans placeholder:text-slate-400 focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          ].join(' ')}
        />
      </div>

      {shown ? (
        <p id={`${id}-err`} className="text-xs text-red-600" role="alert">
          {shown}
        </p>
      ) : null}
    </div>
  );
}
